import React, { useEffect, useRef, useState } from 'react';
import { Box, CircularProgress, Paper, TextField, Typography } from '@mui/material';
import axios from 'axios';
import { apiClient } from '../api/client';
import { PATIENT_STAFF_MEMO_MAX_LENGTH } from '../constants/patient';

type Props = {
  patientId: number;
  initialValue: string;
  readOnly?: boolean;
  onSaved?: (value: string) => void;
};

/** 患者情報画面右側のフリーメモ（入力後に自動保存） */
const PatientStaffMemo: React.FC<Props> = ({ patientId, initialValue, readOnly, onSaved }) => {
  const [value, setValue] = useState(initialValue);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);
  const skipSaveRef = useRef(true);
  const savedTimerRef = useRef<number | null>(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  useEffect(() => {
    setValue(initialValue);
    skipSaveRef.current = true;
  }, [initialValue, patientId]);

  useEffect(() => {
    return () => {
      if (savedTimerRef.current != null) window.clearTimeout(savedTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (readOnly) return;
    if (skipSaveRef.current) {
      skipSaveRef.current = false;
      return;
    }

    const timer = window.setTimeout(() => {
      void (async () => {
        setSaving(true);
        setError('');
        setSavedFlash(false);
        try {
          await apiClient.patch(`/api/patients/${patientId}`, { staff_memo: value });
          onSavedRef.current?.(value);
          setSavedFlash(true);
          if (savedTimerRef.current != null) window.clearTimeout(savedTimerRef.current);
          savedTimerRef.current = window.setTimeout(() => setSavedFlash(false), 2000);
        } catch (e: unknown) {
          if (axios.isAxiosError(e) && e.response?.data?.error) {
            setError(String(e.response.data.error));
          } else {
            setError('保存に失敗しました');
          }
        } finally {
          setSaving(false);
        }
      })();
    }, 800);

    return () => window.clearTimeout(timer);
  }, [value, readOnly, patientId]);

  return (
    <Paper
      variant="outlined"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        borderRadius: 1.5,
        height: { xs: 240, lg: 'calc(100vh - 180px)' },
        minHeight: 200,
        position: { lg: 'sticky' },
        top: { lg: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          px: 1.25,
          py: 0.75,
          borderBottom: 1,
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Typography variant="subtitle2">フリーメモ</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, minHeight: 20 }}>
          {saving && <CircularProgress size={14} />}
          {!saving && savedFlash && (
            <Typography variant="caption" color="success.main">
              保存しました
            </Typography>
          )}
        </Box>
      </Box>

      <Box sx={{ flex: 1, p: 1.25, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <TextField
          multiline
          fullWidth
          placeholder="院内用のメモ"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={readOnly}
          error={Boolean(error)}
          inputProps={{ maxLength: PATIENT_STAFF_MEMO_MAX_LENGTH }}
          helperText={
            error ||
            `${value.length} / ${PATIENT_STAFF_MEMO_MAX_LENGTH} 文字${readOnly ? '（閲覧のみ）' : ''}`
          }
          FormHelperTextProps={{ sx: { mx: 0, mt: 0.75 } }}
          sx={{
            flex: 1,
            '& .MuiInputBase-root': {
              height: '100%',
              alignItems: 'stretch',
              fontSize: '0.8125rem',
              bgcolor: 'background.paper',
            },
            '& .MuiInputBase-inputMultiline': {
              height: '100% !important',
              overflow: 'auto !important',
              boxSizing: 'border-box',
            },
          }}
        />
      </Box>
    </Paper>
  );
};

export default PatientStaffMemo;
